<template>
  <div class="camera-module" :class="{ 'collapsed': isCollapsed }">
    <div class="sci-fi-title" @click="toggleCollapse" :title="isCollapsed ? langMap.dashboard_expandModule[lang] : langMap.dashboard_collapseModule[lang]">
      <div class="title-bg"></div>
      <h3>
        <Icon type="ios-videocam" /> {{ langMap.dashboard_cameraModule[lang]}}
        <Icon :type="isCollapsed ? 'ios-arrow-down' : 'ios-arrow-up'" class="collapse-icon" />
      </h3>
      <div class="title-decoration"></div>
    </div>
    
    <div class="cameras-grid" v-show="!isCollapsed">
      <!-- 无数据提示 -->
      <div v-if="cameraData.length === 0" class="no-data-state">
        <div class="hologram-container">
          <div class="hologram-ring ring-1"></div>
          <div class="hologram-ring ring-2"></div>
          <div class="hologram-ring ring-3"></div>
          <div class="hologram-icon">
            <Icon type="ios-videocam" />
          </div>
        </div>
        <div class="no-data-text">
          <div class="main-text">{{ langMap.dashboard_noData[lang] }}</div>
          <div class="sub-text">{{ langMap.dashboard_loading[lang] }}</div>
        </div>
      </div>
      
      <!-- 正常数据 -->
      <div 
        v-for="camera in cameraData" 
        :key="camera.deviceId"
        class="camera-card"
      >
        <div class="camera-title">
          <span class="camera-name-main">{{ camera.name }}</span>
          <span class="camera-name-sub">({{ camera.deviceName }})</span>
        </div>
        <div class="params-grid">
          <div 
            v-for="param in camera.params" 
            :key="param.paramName"
            class="param-item"
          >
            <div class="param-label">{{ param.label }}</div>
            <div class="param-value">{{ param.value }}<span class="unit">{{ param.unit }}</span></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import collapsibleMixin from './collapsible-mixin';
import langMap from '@/utils/language';

export default {
  name: 'CameraModule',
  mixins: [collapsibleMixin],
  props: {
    paramData: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      langMap: langMap,
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    cameraData() {
      // 筛选相机相关数据并按设备分�?
      const cameras = {};
      
      this.paramData.forEach(item => {
        if (item.deviceName && item.deviceName.includes('camera')) {
          if (!cameras[item.deviceId]) {
            cameras[item.deviceId] = {
              deviceId: item.deviceId,
              deviceName: item.deviceName,
              name: this.lang === 'zh' ? item.deviceNameCn : item.deviceNameEn,
              params: []
            };
          }
          
          cameras[item.deviceId].params.push({
            paramName: item.paramName,
            label: this.lang === 'zh' ? item.paramNameCn : item.paramNameEn,
            value: this.formatValue(item.data),
            unit: item.paramUnit
          });
        }
      });
      
      return Object.values(cameras);
    }
  },
  methods: {
    formatValue(value) {
      // 处理数字或字符串类型的数值
      if (value === null || value === undefined || value === '') {
        return value;
      }
      
      // 尝试转换为数字
      const numValue = typeof value === 'number' ? value : parseFloat(value);
      
      // 如果是有效的数字
      if (!isNaN(numValue)) {
        // 检查是否有小数部分且超过2位
        const decimalPart = (numValue.toString().split('.')[1] || '');
        if (decimalPart.length > 2) {
          return numValue.toFixed(2);
        }
        return numValue;
      }
      
      // 如果不是数字，返回原值
      return value;
    }
  }
}
</script>

<style lang="less" scoped>
.camera-module {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, rgba(0, 21, 41, 0.95), rgba(0, 10, 25, 0.95));
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5),
              inset 0 1px 0 rgba(0, 240, 255, 0.1);
  border: 1px solid rgba(0, 240, 255, 0.3);
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
  min-height: 0;
  
  &.collapsed {
    flex: 0 0 auto;
  }
  
  .sci-fi-title {
    padding: 12px 16px;
    background: linear-gradient(135deg, rgba(0, 40, 80, 0.8), rgba(0, 21, 41, 0.9));
    border-bottom: 2px solid rgba(0, 240, 255, 0.5);
    position: relative;
    z-index: 10;
    cursor: pointer;
    user-select: none;
    transition: all 0.3s ease;
    
    &:hover {
      background: linear-gradient(135deg, rgba(0, 50, 90, 0.9), rgba(0, 30, 50, 0.95));
      border-bottom-color: rgba(0, 240, 255, 0.8);
    }
    
    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 700;
      color: #00f0ff;
      text-shadow: 0 0 15px rgba(0, 240, 255, 0.8);
      letter-spacing: 2px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      
      .collapse-icon {
        font-size: 18px;
        transition: transform 0.3s ease;
        margin-left: auto;
      }
    }
    
    .title-bg {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(90deg, transparent, rgba(0, 240, 255, 0.1), transparent);
      opacity: 0;
      transition: opacity 0.3s ease;
    }
    
    &:hover .title-bg {
      opacity: 1;
    }
    
    .title-decoration {
      position: absolute;
      bottom: -2px;
      left: 50%;
      transform: translateX(-50%);
      width: 60%;
      height: 2px;
      background: linear-gradient(90deg, transparent, #00f0ff, transparent);
      box-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(0, 240, 255, 1) 50%, 
      transparent
    );
    box-shadow: 0 0 20px rgba(0, 240, 255, 0.8);
    animation: scanline 2s linear infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg,
      transparent,
      rgba(0, 240, 255, 0.1),
      transparent
    );
    animation: dataflow 3s ease-in-out infinite;
  }
  
  .cameras-grid {
    flex: 1;
    padding: 8px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-auto-rows: min-content;
    gap: 8px;
    overflow-y: auto;
    overflow-x: hidden;
    align-content: start;
    min-height: 0;
    
    &::-webkit-scrollbar {
      width: 4px;
    }
    
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
      background: rgba(0, 240, 255, 0.3);
      border-radius: 2px;
      
      &:hover {
        background: rgba(0, 240, 255, 0.5);
      }
    }
  }
  
  .camera-card {
    background: linear-gradient(135deg, rgba(0, 40, 80, 0.6), rgba(0, 20, 45, 0.8));
    border: 1px solid rgba(0, 240, 255, 0.4);
    border-radius: 8px;
    padding: 8px 10px;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: visible;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3),
                inset 0 1px 0 rgba(0, 240, 255, 0.2);
    
    &::before {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: conic-gradient(
        transparent,
        rgba(0, 240, 255, 0.1),
        transparent 30%
      );
      animation: rotate 8s linear infinite;
      opacity: 0.5;
    }
    
    .camera-title {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 6px;
      padding-bottom: 4px;
      border-bottom: 1px solid rgba(0, 240, 255, 0.3);
      position: relative;
      z-index: 1;
      justify-content: center;
      
      .camera-name-main {
        font-size: 11px;
        font-weight: 600;
        color: #00f0ff;
        text-shadow: 0 0 10px rgba(0, 240, 255, 0.6);
        letter-spacing: 1px;
        white-space: pre-wrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      
      .camera-name-sub {
        font-size: 8px;
        color: rgba(255, 255, 255, 0.4);
        font-family: 'Consolas', monospace;
        white-space: nowrap;
      }
    }
    
    .params-grid {
      display: flex;
      flex-direction: column;
      gap: 4px;
      position: relative;
      z-index: 1;
      
      .param-item {
        background: linear-gradient(135deg, rgba(0, 240, 255, 0.1), rgba(0, 180, 255, 0.05));
        border: 1px solid rgba(0, 240, 255, 0.3);
        border-radius: 6px;
        padding: 4px 8px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        position: relative;
        overflow: hidden;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2),
                    inset 0 1px 0 rgba(0, 240, 255, 0.1);
        
        
        .param-label {
          font-size: 9px;
          color: rgba(255, 255, 255, 0.6);
          white-space: nowrap;
          letter-spacing: 0.5px;
        }
        
        .param-value {
          font-size: 14px;
          font-weight: 700;
          color: #fff;
          font-family: 'Consolas', 'Monaco', monospace;
          white-space: nowrap;
          text-shadow: 0 0 15px rgba(255, 255, 255, 0.6),
                       0 0 30px rgba(0, 240, 255, 0.4);
          transition: all 0.3s ease;
          position: relative;
          display: inline-block;
          
          .unit {
            font-size: 10px;
            color: rgba(0, 240, 255, 1);
            margin-left: 3px;
            font-weight: 600;
            text-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
          }
        }
      }
    }
  }
  
  // 无数据状态
  .no-data-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
    padding: 48px 24px;
    min-height: 200px;
    grid-column: span 2;

    .hologram-container {
      position: relative;
      width: 120px;
      height: 120px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      .hologram-ring {
        position: absolute;
        border: 2px solid rgba(0, 240, 255, 0.4);
        border-radius: 50%;
        
        &.ring-1 {
          width: 120px;
          height: 120px;
          animation: pulse-ring 2s ease-in-out infinite;
        }
        
        &.ring-2 {
          width: 90px;
          height: 90px;
          animation: pulse-ring 2s ease-in-out 0.3s infinite;
        }
        
        &.ring-3 {
          width: 60px;
          height: 60px;
          animation: pulse-ring 2s ease-in-out 0.6s infinite;
        }
      }
      
      .hologram-icon {
        position: relative;
        z-index: 1;
        font-size: 48px;
        color: #00f0ff;
        text-shadow: 0 0 20px rgba(0, 240, 255, 0.8),
                     0 0 40px rgba(0, 240, 255, 0.6),
                     0 0 60px rgba(0, 240, 255, 0.4);
        animation: float 3s ease-in-out infinite;
        
        i {
          display: block;
        }
      }
    }
    
    .no-data-text {
      text-align: center;
      
      .main-text {
        font-size: 16px;
        font-weight: 600;
        color: #00f0ff;
        text-shadow: 0 0 10px rgba(0, 240, 255, 0.6);
        margin-bottom: 8px;
        letter-spacing: 1px;
      }
      
      .sub-text {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.5);
        animation: pulse-text 2s ease-in-out infinite;
      }
    }
  }
}

@keyframes pulse-ring {
  0%, 100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes pulse-text {
  0%, 100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes scanline {
  0% {
    transform: translateY(0);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(500px);
    opacity: 0;
  }
}

@keyframes dataflow {
  0% {
    left: -100%;
    opacity: 0;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    left: 100%;
    opacity: 0;
  }
}
</style>




